/**
 * Services des papiers d'équipe.
 *
 * Le bureau les tient : lire, enregistrer, retirer. Le compagnon lit les siens
 * par `mes_papiers` — la base borne la réponse à ses propres lignes, ce verbe
 * n'ajoute que le filtre qui rend la réponse juste pour un compte de bureau
 * rattaché à un salarié (un chef d'équipe qui a aussi des papiers).
 */
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { Certification } from "./types.js";
import type {
  ListCertificationsInput,
  RemoveCertificationInput,
  SaveCertificationInput,
} from "./schema.js";

export interface CertificationRepository {
  list(ctx: ExecutionContext, employeeId?: string): Promise<Certification[]>;
  /** Ceux du salarié rattaché au compte appelant ; vide s'il n'y en a pas. */
  listMine(ctx: ExecutionContext): Promise<Certification[]>;
  save(
    ctx: ExecutionContext,
    command: Omit<SaveCertificationInput, "confirmationId">,
  ): Promise<Certification>;
  remove(ctx: ExecutionContext, id: string): Promise<void>;
}

const BUREAU = ["patron", "conducteur", "assistant"] as const;

export function createCertificationServices(repo: CertificationRepository) {
  const listerPapiers = defineService<ListCertificationsInput, Certification[]>({
    name: "lister_papiers",
    level: ActionLevel.Read,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.list(ctx, input.employeeId),
  });

  const mesPapiers = defineService<Record<string, never>, Certification[]>({
    name: "mes_papiers",
    level: ActionLevel.Read,
    handler: (ctx) => repo.listMine(ctx),
  });

  const enregistrerPapier = defineService<SaveCertificationInput, Certification>({
    name: "enregistrer_papier",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => {
      const { confirmationId: _ignored, ...command } = input;
      return repo.save(ctx, command);
    },
  });

  const supprimerPapier = defineService<RemoveCertificationInput, void>({
    name: "supprimer_papier",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.remove(ctx, input.id),
  });

  return { listerPapiers, mesPapiers, enregistrerPapier, supprimerPapier };
}

export type CertificationServices = ReturnType<typeof createCertificationServices>;
